/**
 * Aday konsey kataloğu — adım 3'ün "Adaylardan Seç" kolu.
 *
 * Operatör geri bildirimi (2026-08): "Konsey üyelerini istediğim gibi
 * belirleyemiyorum. Otomatik üretim çok finansal jargona kaçtı; kendim
 * seçince bu kez teknik ağırlıklı oldu ve donanımsal kriptolama gibi
 * konulara daldılar. Belki 20-30 başlıkta üye önerse, ben içinden seçsem."
 *
 * Bu panel o eksik adımdır: sistem soruyu, yüklenen belgeleri ve adım
 * 2'deki Konsey Yönergesi'ni okuyup kategorilere yayılmış adaylar üretir;
 * kararı operatör verir. Seçim onaylandığında her aday `from-title` ile
 * gerçek bir personaya dönüşür.
 */
import { useEffect } from "react";
import { Check, Loader2, RefreshCw, Sparkles, Users } from "lucide-react";

import { useDebateStore } from "../../store/debateStore";
import { Button } from "../common/Button";
import { RoleIcon, roleLabel } from "../common/RoleIcon";
import { cn } from "../common/cn";
import { WIZARD_LIMITS } from "../../domain/wizard";

export interface CouncilCandidatePickerProps {
  disabled?: boolean;
}

export function CouncilCandidatePicker({
  disabled = false,
}: CouncilCandidatePickerProps): JSX.Element {
  const candidates = useDebateStore((s) => s.wizard.candidates);
  const query = useDebateStore((s) => s.wizard.query);
  const personaCount = useDebateStore((s) => s.wizard.personas.length);
  const generationStatus = useDebateStore((s) => s.wizard.generationStatus);
  const loadCandidates = useDebateStore((s) => s.loadCouncilCandidates);
  const toggleCandidate = useDebateStore((s) => s.toggleCandidate);
  const selectRecommended = useDebateStore((s) => s.selectRecommendedCandidates);
  const materialize = useDebateStore((s) => s.materializeCandidates);

  const stale =
    candidates.status === "ready" && candidates.builtForQuery !== query.trim();

  useEffect(() => {
    if (disabled) return;
    if (candidates.status === "idle" || stale) {
      void loadCandidates(stale);
    }
  }, [disabled, candidates.status, stale, loadCandidates]);

  const isLoading = candidates.status === "loading";
  const isBuilding = generationStatus === "generating";
  const selectedCount = candidates.selected.length;
  const enoughSelected = selectedCount >= WIZARD_LIMITS.agentCountMin;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-line-soft/50 bg-surface-2/20 px-4 py-10 text-center">
        <Loader2 className="h-5 w-5 animate-spin text-fg-muted" aria-hidden />
        <p className="text-[12.5px] font-medium text-fg-strong">
          Adaylar hazırlanıyor…
        </p>
        <p className="max-w-md text-[11.5px] leading-relaxed text-fg-subtle">
          Soru, yüklediğin belgeler ve Konsey Yönergesi okunuyor; masaya
          oturması anlamlı olacak uzman türleri çıkarılıyor.
        </p>
      </div>
    );
  }

  if (candidates.status === "error") {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3">
        <span className="text-[12px] leading-relaxed text-rose-200">
          {candidates.error ?? "Aday listesi alınamadı."}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void loadCandidates(true)}
          disabled={disabled}
        >
          <RefreshCw className="h-4 w-4" /> Tekrar dene
        </Button>
      </div>
    );
  }

  if (candidates.items.length === 0) return <></>;

  const byCategory = candidates.categories
    .map((category) => ({
      category,
      items: candidates.items.filter((c) => c.category === category),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-fg-strong">
            Aday üyeler
            <span className="ml-2 rounded-full border border-white/10 bg-surface-2/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-fg-subtle">
              {selectedCount} seçili / {candidates.items.length} aday
            </span>
          </div>
          <div className="text-xs text-fg-subtle">
            İşaretli olanlar sistemin önerdiği çekirdek kadro. İstediğini
            ekle, istemediğini çıkar.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={selectRecommended}
            disabled={disabled || isBuilding}
          >
            <Sparkles className="h-4 w-4" /> Öneriye dön
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void loadCandidates(true)}
            disabled={disabled || isBuilding}
          >
            <RefreshCw className="h-4 w-4" /> Yeni liste
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {byCategory.map((group) => (
          <section key={group.category}>
            <h4 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-fg-subtle">
              {group.category}
            </h4>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {group.items.map((candidate) => {
                const checked = candidates.selected.includes(candidate.id);
                return (
                  <button
                    key={candidate.id}
                    type="button"
                    disabled={disabled || isBuilding}
                    onClick={() => toggleCandidate(candidate.id)}
                    aria-pressed={checked}
                    className={cn(
                      "focus-ring flex gap-2.5 rounded-lg border p-2.5 text-left transition-colors",
                      checked
                        ? "border-accent-300/60 bg-[hsl(var(--accent)/0.12)]"
                        : "border-line-soft/60 bg-surface-2/30 hover:border-accent-300/40",
                      (disabled || isBuilding) && "opacity-60",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                        checked
                          ? "border-accent-300/70 bg-[hsl(var(--accent)/0.35)]"
                          : "border-line-soft/70",
                      )}
                      aria-hidden
                    >
                      {checked ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-[12.5px] font-medium text-fg-strong">
                          {candidate.title}
                        </span>
                        {candidate.recommended ? (
                          <span className="shrink-0 rounded-full border border-accent-300/40 px-1.5 text-[9px] font-semibold uppercase tracking-wider text-accent-200">
                            önerilen
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block text-[11px] leading-relaxed text-fg-subtle">
                        {candidate.focus}
                      </span>
                      <span className="mt-1 flex items-center gap-1 text-[10px] text-fg-subtle">
                        <RoleIcon role={candidate.role} className="h-3 w-3" />
                        {roleLabel(candidate.role)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 border-t border-line-soft/40 bg-surface-1/90 pt-3 backdrop-blur">
        <span className="text-[11.5px] text-fg-subtle">
          {enoughSelected
            ? `${selectedCount} üyelik konsey kurulacak.`
            : `En az ${WIZARD_LIMITS.agentCountMin} üye seç.`}
        </span>
        <Button
          variant="primary"
          size="md"
          onClick={() => void materialize()}
          disabled={disabled || isBuilding || !enoughSelected}
        >
          {isBuilding ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Konsey kuruluyor… {candidates.materializedCount}/{selectedCount}
            </>
          ) : (
            <>
              <Users className="h-4 w-4" />
              {personaCount > 0 ? "Konseyi yeniden kur" : "Konseyi kur"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
